import { useCurrency } from '@/currency/store';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router';
import { isLocale, detectLocale, readStoredLocale, DEFAULT_LOCALE, type Locale } from '@/i18n/locales';
import { applyCulture } from '@/i18n/culture';
import { I18nProvider } from '@/i18n/I18nProvider';
import { useSession } from '@/stores/session';
import { bootstrapSession } from '@/stores/actions';
import { useThemeSync } from '@/theme/theme';
import { useFxSync, useMusicRoute } from '@/fx';
import { connectSocket } from '@/socket/socket';
import { ConnectionBanner, Toaster } from '@/ui';
import { DailyBonusModal } from '@/features/bonus/DailyBonusModal';
import { useDailyBonus } from '@/features/bonus/useDailyBonus';
import { useSeasonStore } from '@/features/season/useSeason';
import { GlobalHosts } from '@/features/errors/GlobalHosts';
import { SocialListener } from '@/features/social/Progression';
import { BadgesListener } from '@/features/social/BadgesListener';
import { ChatListener } from '@/features/chat/ChatListener';
import { TourneyListener } from '@/features/tourney/TourneyListener';
import { AchievementListener } from '@/features/achievements/AchievementListener';
import { JackpotListener } from '@/features/jackpot/JackpotListener';
import { PassportListener } from '@/features/passport/PassportListener';
import { QuestListener } from '@/features/quests/QuestListener';
import { ShortLink } from '@/features/share/ShortLink';
import { PageviewBeacon } from '@/features/analytics/PageviewBeacon';
import { LastGameTracker } from '@/skill/recent';
import { SkillListener } from '@/skill/SkillListener';
import { OAuthLanding } from '@/features/auth/oauth/landing';
import { GAME_PAGES, LOBBY_PAGE, SKILL_PAGES, SLOT_PAGES, SYSTEM_PAGES } from './routes';
import { gamePathOf, type SkillGameId } from '@tg/shared';

/** "/" → the best language: saved choice → browser languages → en. */
export function RootRedirect({ languages }: { languages?: readonly string[] }) {
  const stored = readStoredLocale();
  const target: Locale = stored ?? detectLocale(languages ?? (typeof navigator !== 'undefined' ? navigator.languages : []));
  return <Navigate to={`/${target}`} replace />;
}

/** /:lang/* — unknown language → /en/<rest>; otherwise provides i18n for the subtree. */
export function LocaleGate() {
  const { lang } = useParams();
  const loc = useLocation();
  const setLocale = useSession((s) => s.setLocale);
  const valid = isLocale(lang);
  useEffect(() => {
    if (!valid) return;
    setLocale(lang);
    applyCulture(lang);
  }, [valid, lang, setLocale]);
  if (!valid) {
    const rest = loc.pathname.split('/').slice(2).join('/');
    return <Navigate to={`/${DEFAULT_LOCALE}${rest ? `/${rest}` : ''}${loc.search}`} replace />;
  }
  return (
    <I18nProvider locale={lang}>
      <Chrome />
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </I18nProvider>
  );
}

/** Global overlays that need i18n: offline banner, toasts, daily bonus dialog, game errors, reality check, achievement bar, jackpot win, page-view beacon; music by route. */
function Chrome() {
  useMusicRoute();
  return (
    <>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 900 }}>
        <ConnectionBanner />
      </div>
      <Toaster />
      <DailyBonusModal />
      <GlobalHosts />
      <SocialListener />
      <BadgesListener />
      <ChatListener />
      <TourneyListener />
      <AchievementListener />
      <JackpotListener />
      <PassportListener />
      <QuestListener />
      <PageviewBeacon />
      <LastGameTracker />
      <SkillListener />
      <OAuthLanding />
    </>
  );
}

const NotFound = lazy(() => import('@/features/errors/NotFoundPage'));

/** Route table (also used by tests with a MemoryRouter). */
export function AppRoutes() {
  const Lobby = LOBBY_PAGE;
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/cookies" element={<Navigate to="/en/cookies" replace />} />
      <Route path="/cooke" element={<Navigate to="/en/cookies" replace />} />
      {/* short share links (QR codes, messengers) → /<lang>/… keeping ?c= */}
      <Route path="/i/:code" element={<ShortLink kind="i" />} />
      <Route path="/a/:id" element={<ShortLink kind="a" />} />
      <Route path="/:lang" element={<LocaleGate />}>
        <Route index element={<Lobby />} />
        {Object.entries(GAME_PAGES).map(([id, C]) => (
          <Route key={id} path={id} element={<C />} />
        ))}
        {Object.entries(SLOT_PAGES).map(([id, C]) => (
          <Route key={id} path={`slots/${id}`} element={<C />} />
        ))}
        {Object.entries(SKILL_PAGES).map(([id, C]) => (
          <Route key={id} path={gamePathOf(id as SkillGameId).slice(1)} element={<C />} />
        ))}
        {Object.entries(SYSTEM_PAGES).map(([id, C]) => (
          <Route key={id} path={id} element={<C />} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/** Root: session bootstrap, socket, theme and FX sync. */
export function App() {
  useEffect(() => {
    const refresh = () => {
      if (!document.hidden) void useCurrency.getState().load();
    };
    refresh();
    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);
  useThemeSync();
  useFxSync();
  const status = useSession((s) => s.status);
  useEffect(() => {
    void bootstrapSession().then(() => {
      connectSocket();
      void useDailyBonus.getState().load();
      void useSeasonStore.getState().load();
    });
  }, []);
  useEffect(() => {
    if (status !== 'loading') void useDailyBonus.getState().load();
  }, [status]);
  return <AppRoutes />;
}
